import React, { Component } from 'react';
import { connect } from 'redux-scaffolding-ts';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Categories } from 'stores/requests/requests-store';
import { Button, Form, Input } from 'semantic-ui-react';
import { LocationItemReference } from 'widgets/bussiness/location-editor';
import TailwindLocationEditor from 'widgets/bussiness/location-editor-tw';
import TailwindEventTypeEditor from 'widgets/bussiness/event-type-editor-tw';
import TailwindPriorityEditor from 'widgets/bussiness/priority-editor-tw';
import TailwindProfessionEditor from 'widgets/bussiness/profession-editor-tw';
import {
  EventFieldGroups,
  EventTypesStore,
  EventTypeInstructorConfiguration,
  EventTypeCategory
} from 'stores/configuration/events-workflow/event-types-store';
import { RequestWizardStore, RequestWizardData } from 'stores/requests/create-request-wizard-store';
import { IdentityService } from 'services/identity-service';
import { resolve } from 'inversify.config';
import TailwindLocationEditorWithRoleLocations from 'widgets/bussiness/location-editor-with-rolelocations-tw';
import './step1-basic.less';
import ReasonLinkedEventsPopup from './reason-linked-events-popup';

import { LocationsStore } from 'stores/configuration/locations/locations-store';
import { Query, ItemReference } from 'stores/dataStore';
import TailwindRequestReasonEditor from 'widgets/bussiness/request-reason-editor-tw';
import { isExtendedWorkflow } from 'utils/event-type-utils';
import { RequestReasonDto } from 'stores/configuration/events-n-requests/request-reasons-store';

interface TailwindStepBasicProps extends WithTranslation {
  onRestartSteps: () => void;
  requestWizardStore?: RequestWizardStore;
  selectLocation: LocationsStore;
  selectedEventType?: EventTypesStore;
  onSelectEventTypeBegin?: () => void;
  onSelectEventTypeEnd?: () => void;
}

interface TailwindStepBasicState {
  query: Query;
  selectedLocation: string;
  locationsRecived: string[];
  isMachineRelated: boolean;
  isRequestDetails: boolean;
  isEventDetails: boolean;
  isInstructorYes: boolean;
  selectedReasonRequest: boolean;
  reasonRequestEventTypes: string[];
  isOpenedUsersModal: boolean;
}

@connect(['selectedEventType', EventTypesStore], ['requestWizardStore', RequestWizardStore], ['selectLocation', LocationsStore])
class TailwindStepBasic extends Component<TailwindStepBasicProps, TailwindStepBasicState> {
  private get requestWizardStore() {
    return this.props.requestWizardStore;
  }

  private get selectedEventType() {
    return this.props.selectedEventType;
  }

  @resolve(IdentityService)
  private identityService: IdentityService;

  constructor(props) {
    super(props);
    let query: Query = { searchQuery: '', orderBy: [], skip: 0, take: 1000, parameters: { $select: 'id' } };
    this.state = {
      query,
      selectedLocation: '',
      locationsRecived: [],
      isMachineRelated: false,
      isRequestDetails: false,
      isEventDetails: false,
      isInstructorYes: false,
      selectedReasonRequest: false,
      reasonRequestEventTypes: [],
      isOpenedUsersModal: false
    };
  }

  private handleOnOpenUserModal = () => {
    this.setState({ isOpenedUsersModal: true });
  };

  private handleOnCloseUserModal = () => {
    this.setState({ isOpenedUsersModal: false });
  };

  componentDidMount() {
    this.load();
  }

  private load = () => {
    const currentUserInfo = this.identityService.getUserInfo();

    if (IdentityService.isRoleWithLocations(currentUserInfo)) {
      const rolesLocations = currentUserInfo.locationsByRoles[currentUserInfo.activeRole] as string[];
      if (this.identityService.hasRoleMultipleLocations(currentUserInfo)) {
        this.setState({ locationsRecived: rolesLocations });
      } else {
        this.props.selectLocation.getUserById(rolesLocations[0]).then(({ location, id }) => {
          this.setState({ selectedLocation: location }, () => this.requestWizardStore.change({ requestingLocationId: id }));
          this.changeLocationEvent({ id: id, title: location });
        });
      }
    }
  };

  private changeLocationEvent = (location: ItemReference | LocationItemReference) => {
    this.handleValue('requestingLocationId', location ? location.id : null);
    this.handleValue('requestLocation', location);
  };

  private handleValue = (property: keyof RequestWizardData, value: any) => {
    let change: Partial<RequestWizardData> = {};
    change[property as string] = value;
    change['title'] = '';
    if (property === 'isMachineRelated' || property === 'category') {
      change.nmrCluster = null;
      change.nmrFunctionalArea = null;
      change.nmrTrainingName = null;
      change.nmrFunctionalSubArea = null;
    }

    if (property === 'eventType') {
      if (change.eventType && EventTypeCategory[change.eventType.eventTypeCategory] === EventTypeCategory.Extended) {
        change.isMachineRelated = true;
        change.isTechnical = true;
        change.category = Categories.Technical;
      } else {
        change.isMachineRelated = null;
        change.isTechnical = null;
        change.category = null;
      }
      change = { ...change, ...this.refreshStep(value) };
    }
    this.requestWizardStore.change({ ...this.requestWizardStore.state.item, ...change });
  };

  private toggleCategory = (category: number) => {
    const { category: cat } = this.requestWizardStore.state.item;
    if (category === 10 && (!cat || cat === Categories.Functional)) {
      this.handleValue('category', Categories.Technical);
      this.handleValue('isTechnical', true);
    } else if (category === 20 && (!cat || cat === Categories.Technical)) {
      this.setState({ isMachineRelated: false });
      this.handleValue('isMachineRelated', false);
      this.handleValue('category', Categories.Functional);
      this.handleValue('isTechnical', false);
    }
  };

  private toggleMachineRelated = (mr: number) => {
    const { isMachineRelated } = this.requestWizardStore.state.item;
    if (mr === 0 && !isMachineRelated) {
      this.handleValue('category', Categories.Technical);
      this.handleValue('isTechnical', true);
      this.handleValue('isMachineRelated', true);
      this.setState({ isMachineRelated: true });
    } else if (mr === 1 && (isMachineRelated == null || isMachineRelated)) {
      this.handleValue('isMachineRelated', false);
      this.setState({ isMachineRelated: false });
    }
  };

  private refreshStep = (response: any): Partial<RequestWizardData> => {
    const isRequestDetails = (response?.requestFieldGroups || []).includes(EventFieldGroups[EventFieldGroups.RequestDetails]);
    const isEventDetails = (response?.requestFieldGroups || []).includes(EventFieldGroups[EventFieldGroups.EventDetails]);
    const isInstructorYes = response?.instructor === EventTypeInstructorConfiguration[EventTypeInstructorConfiguration.Yes];
    const changes: Partial<RequestWizardData> = {};

    changes.isParticipantYes = !!response?.participants;
    changes.hasSupportDetails = (response?.requestFieldGroups || []).includes(EventFieldGroups[EventFieldGroups.SupportDetails]);
    changes.isEventDetails = isEventDetails;
    changes.isInstructorYes = isInstructorYes;
    changes.isRequestDetails = isRequestDetails;

    if (!isRequestDetails) {
      changes.eventLocation = null;
      changes.priority = null;
      changes.category = null;
      changes.isTechnical = null;
      changes.role = null;
      changes.isMachineRelated = null;
    }

    this.setState({ isInstructorYes, isRequestDetails, isEventDetails });
    return changes;
  };

  private isAdminPlannerOrPocMultipleLocs(): boolean {
    const t = IdentityService.isAdminOrPlanner(this.identityService.getUserInfo()) || this.identityService.hasPocMultipleLocations();
    return t;
  }

  handleOnAdddRequestReason = (requestReason: RequestReasonDto) => {
    let r: ItemReference = { id: requestReason.id, title: requestReason.name };
    this.handleValue('requestReason', r);
    this.setState({
      reasonRequestEventTypes: requestReason ? requestReason.eventTypes.map(i => i.originalEventTypeId) : [],
      selectedReasonRequest: requestReason != null
    });
  };

  render() {
    const { t, requestWizardStore } = this.props;
    const { item } = requestWizardStore.state;
    const currentUser = this.identityService.getUserInfo();
    const isRoleWithLocations = IdentityService.isRoleWithLocations(currentUser);
    const showMultipleLocations = !IdentityService.isRoleWithLocations(currentUser);
    const { isOpenedUsersModal } = this.state;

    return (
      <>
        <Form>
          <div className="required field mb-3">
            <div className="required field request-reason-container">
              <label className="block text-sm text-gray-700 mb-1">{t('Request Reason')}</label>
              <button className="request-reason-button text-white px-2 rounded" onClick={this.handleOnOpenUserModal}>
                <svg
                  className="w-6 h-6 text-gray-800 dark:text-white"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              </button>
            </div>
            <TailwindRequestReasonEditor
              className="w-full break-words whitespace-normal"
              nullable
              value={item.requestReason}
              onChange={r => this.handleValue('requestReason', r)}
              onSelected={r => {
                this.setState({
                  reasonRequestEventTypes: r ? r.eventTypes.map(i => i.originalEventTypeId) : [],
                  selectedReasonRequest: r != null
                });
              }}
            />
          </div>

          <div className="required field mb-3">
            <label className="block text-sm text-gray-700 mb-1">{t('Event Type')}</label>
            <TailwindEventTypeEditor
              nullable
              reloadOnChange
              readOnly={!this.state.selectedReasonRequest}
              value={item.eventType}
              onChange={c => this.handleValue('eventType', c?.id ? c : null)}
              filterRequestCreator={true}
              filterEventTypesIds={this.state.reasonRequestEventTypes}
            />
          </div>

          {item.eventType && EventTypeCategory[item.eventType.eventTypeCategory] === EventTypeCategory.Extended && (
            <div className="required field mb-3">
              <label className="block text-sm text-gray-700 mb-1">{t('Support Need Explanation')}</label>
              <textarea
                className="w-full p-2 border border-gray-300 rounded"
                onChange={value => this.handleValue('requestReasonComments', value.target.value as string)}
                value={item.requestReasonComments ? item.requestReasonComments : ''}
                placeholder="Please explain the driver of this request briefly"
                rows={4}
              />
            </div>
          )}

          {item.eventType && isExtendedWorkflow(item.eventType.eventTypeCategory) && (
            <div className="mb-3">
              <label className="block text-sm text-gray-700 mb-1">{t('Request pre-approved during ASP')}</label>
              <input
                id="default-checkbox"
                type="checkbox"
                value=""
                checked={item.requestPreApprovedDuringASPCheck}
                onChange={e => this.handleValue('requestPreApprovedDuringASPCheck', e.target.checked)}
                className="w-4 h-4 text-black-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                disabled={false}
              />
            </div>
          )}

          <div className="required field mb-3">
            <label className="block text-sm text-gray-700 mb-1">{t('Request Location')}</label>
            {showMultipleLocations && (
              <TailwindLocationEditor
                nullable
                value={item.requestLocation}
                onChange={location => this.changeLocationEvent(location)}
                showCountry={true}
              />
            )}

            {isRoleWithLocations && this.identityService.hasRoleMultipleLocations(currentUser) && (
              <TailwindLocationEditorWithRoleLocations
                className="min-width-100pc"
                value={item.requestingLocationId}
                locationsReceived={this.state.locationsRecived}
                onChange={location => this.changeLocationEvent({ id: location.id, title: location.location })}
                showCountry={true}
              />
            )}
            {isRoleWithLocations && !this.identityService.hasRoleMultipleLocations(currentUser) && (
              <Input className="min-width-16em" readOnly={true} value={this.state.selectedLocation} />
            )}
          </div>

          {item.isRequestDetails && (
            <>
              <div className="required field mb-3">
                <label className="block text-sm text-gray-700 mb-1">{t('Event Location')}</label>
                <TailwindLocationEditor
                  nullable
                  value={item.eventLocation}
                  onChange={c => this.handleValue('eventLocation', c)}
                  showCountry={true}
                />
              </div>
              <div className="field mb-3">
                <label className="block text-sm text-gray-700 mb-1">{t('Priority')}</label>
                <TailwindPriorityEditor nullable clearable={true} value={item.priority} onChange={c => this.handleValue('priority', c)} />
              </div>
            </>
          )}

          {item.isRequestDetails && item.eventType && EventTypeCategory[item.eventType.eventTypeCategory] !== EventTypeCategory.Extended && (
            <>
              <div className="required field mb-3">
                <label className="block text-sm text-gray-700 mb-1">{t('Role')}</label>
                <TailwindProfessionEditor nullable value={item.role} onChange={c => this.handleValue('role', c)} />
              </div>

              <div className="required field mb-3">
                <label className="block text-sm text-gray-700 mb-1">{t('Category')}</label>
                <Button.Group className="filter-button-group green" style={{ width: 195 }}>
                  <Button
                    className={item.isTechnical === true ? `active gray-border` : `gray-border`}
                    onClick={() => this.toggleCategory(10)}
                  >
                    {t('Technical')}
                  </Button>
                  <Button
                    className={item.isTechnical === false ? `active gray-border` : `gray-border`}
                    onClick={() => this.toggleCategory(20)}
                  >
                    {t('Functional')}
                  </Button>
                </Button.Group>
              </div>

              <div className="required field mb-3">
                <label className="block text-sm text-gray-700 mb-1">{t('Machine related')}</label>
                <Button.Group className="filter-button-group green" style={{ width: 195 }}>
                  <Button
                    className={item.isMachineRelated === true ? `active gray-border` : `gray-border`}
                    onClick={() => this.toggleMachineRelated(0)}
                  >
                    {t('Yes')}
                  </Button>
                  <Button
                    className={item.isMachineRelated === false ? `active gray-border` : `gray-border`}
                    onClick={() => this.toggleMachineRelated(1)}
                  >
                    {t('No')}
                  </Button>
                </Button.Group>
              </div>
            </>
          )}
        </Form>

        {isOpenedUsersModal && (
          <ReasonLinkedEventsPopup
            selectedRequestReasonId={item.requestReason?.id}
            onCloseUsersModal={this.handleOnCloseUserModal}
            onAdddRequestReason={this.handleOnAdddRequestReason}
          />
        )}
      </>
    );
  }
}

export default withTranslation()(TailwindStepBasic);
